import type { DevDailyCountDto } from '@/api';
import { themeColors } from '@/theme/colors';
import { ThemeProvider, createTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

type Props = {
  points: DevDailyCountDto[];
  ariaLabel: string;
};

const WEEKDAY = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  timeZone: 'UTC',
});

const DATE = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: '2-digit',
  month: '2-digit',
  timeZone: 'UTC',
});

// The page runs inside the Klik (Chakra) theme, which shares the Emotion theme context with MUI.
// MUI X charts read theme.palette, so they need their own MUI theme, as the MUI tables in the app do.
const muiTheme = createTheme({ palette: { mode: 'light' } });

/** Weekday label of a UTC date, for example "Mon". */
export const formatWeekday = (date: Date): string => WEEKDAY.format(date);

/**
 * Line chart of counts per day. The X axis shows the weekday (UTC dates, same as the backend counts),
 * the Y axis the count.
 */
export const DailyCountLineChart = ({ points, ariaLabel }: Props) => (
  <div
    role="img"
    aria-label={ariaLabel}
    style={{ width: '100%', maxWidth: 720 }}
  >
    <ThemeProvider theme={muiTheme}>
      <LineChart
        height={240}
        xAxis={[
          {
            scaleType: 'point',
            data: points.map((p) => p.date),
            valueFormatter: (date: Date, context) =>
              context.location === 'tooltip'
                ? DATE.format(date)
                : formatWeekday(date),
          },
        ]}
        yAxis={[{ min: 0, tickMinStep: 1 }]}
        series={[
          {
            data: points.map((p) => p.count),
            label: 'Prices changed',
            color: themeColors.support.informative.default,
            showMark: true,
          },
        ]}
        grid={{ horizontal: true }}
        hideLegend
        margin={{ left: 8, right: 16, top: 16, bottom: 8 }}
      />
    </ThemeProvider>
  </div>
);
